import { useRef } from "react";
import { IS_IOS, IS_PWA } from "@/lib/environmentDetector";
import { useIosViewportGuard } from "@/hooks/useIosViewportGuard";

/**
 * Telegram-style chrome for iOS standalone: a progressive blur + tint
 * band under the OS status bar, so content that scrolls (or is pushed
 * by the keyboard) beneath the clock reads as deliberate design instead
 * of a rendering bug. The band renders ONLY in standalone — browser
 * tabs manage the status bar with their own chrome, and Chrome on iOS
 * exposes a non-zero top inset in its fullscreen modes, where the band
 * showed up as a stray green blur over Chrome's UI (Catalin, real
 * device, 08-04). The viewport guard the component arms still runs on
 * every iOS surface (shrink-only in tabs — see useIosViewportGuard).
 *
 * Sizing rides on `env(safe-area-inset-top)` (see `.status-bar-scrim`
 * in globals.css), so legacy opaque webclips — inset 0, nothing renders
 * under the clock — collapse it to invisible on their own. Pure chrome:
 * never intercepts input.
 */
export default function StatusBarScrim() {
  const ref = useRef<HTMLDivElement>(null);
  useIosViewportGuard(ref);

  if (!(IS_IOS && IS_PWA)) {
    return null;
  }

  return (
    <div
      ref={ref}
      aria-hidden
      data-testid="status-bar-scrim"
      className="status-bar-scrim"
    />
  );
}
